import { useRef, useState, memo, useEffect, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { updatePostAction } from "../../redux/actions/postActions";
import { getFileUploadSettings } from "../../redux/api/userAPI";
import { useToast } from "../../contexts/ToastContext";
import { FaUserFriends, FaUsers, FaVideo, FaGlobe, FaLock } from "react-icons/fa";

const PublishPostModal = memo(({ showModal, post, onClose }) => {
  const dispatch = useDispatch();
  const { error: showError, success: showSuccess } = useToast();
  const { joinedCommunities } = useSelector((state) => state.community || {});

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    content: post?.content || "",
    audience: post?.audience || "Followers", // Use post's existing audience or default to Followers
    selectedCommunities: post?.audience === "Communities" && post?.communities ? post.communities.map(c => c._id || c) : [],
    file: null,
    youtubeVideo: post?.youtubeVideo || null,
    existingImage: post?.fileUrl || null, // Add existing image from post
    existingFileType: post?.fileType || null, // Add existing file type
    error: "",
    allowComments: post?.allowComments !== undefined ? post.allowComments : true,
  });

  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    url: "",
    isValid: false,
    videoData: null,
    loading: false,
    error: ""
  });

  const [fileUploadSettings, setFileUploadSettings] = useState({
    maxFileSizeMB: null,
    allowedFileTypes: []
  });

  // Fetch file upload settings on component mount
  useEffect(() => {
    const fetchFileUploadSettings = async () => {
      try {
        const { error, data } = await getFileUploadSettings();
        if (!error && data) {
          setFileUploadSettings({
            maxFileSizeMB: data.maxFileSizeMB,
            allowedFileTypes: data.allowedFileTypes
          });
        }
      } catch (error) {
        console.error("Error fetching file upload settings:", error);
      }
    };

    fetchFileUploadSettings();
  }, []);

  // Check if existing file is an image
  const isExistingImageFile = useMemo(() => {
    const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg"];
    
    if (!formData.existingImage) return false;
    
    try {
      const cleanUrl = formData.existingImage.split("?")[0];
      const match = cleanUrl.match(/\.[^.]+$/);
      const fileExtension = match ? match[0].toLowerCase() : "";
      return validExtensions.includes(fileExtension);
    } catch {
      return false;
    }
  }, [formData.existingImage]);

  const handleContentChange = (event) => {
    setFormData({
      ...formData,
      content: event.target.value,
    });
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Check if settings are loaded
    if (!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length) {
      showError("File upload settings are still loading. Please wait a moment and try again.");
      return;
    }

    const maxSizeBytes = fileUploadSettings.maxFileSizeMB * 1024 * 1024;
    
    // Check file size
    if (selectedFile.size > maxSizeBytes) {
      const errorMessage = `Please select a file under ${fileUploadSettings.maxFileSizeMB}MB.`;
      setFormData({
        ...formData,
        file: null,
        error: errorMessage,
      });
      showError(errorMessage);
      return;
    }

    // Check file type
    const fileType = selectedFile.type.split('/')[0];
    if (!fileUploadSettings.allowedFileTypes.includes(fileType)) {
      const allowedTypes = fileUploadSettings.allowedFileTypes.join(' or ');
      const errorMessage = `Please select a ${allowedTypes} file.`;
      setFormData({
        ...formData,
        file: null,
        error: errorMessage,
      });
      showError(errorMessage);
      return;
    }

    // File is valid
    setFormData({
      ...formData,
      file: selectedFile,
      error: "",
    });
  };

  // Remove existing image
  const handleRemoveExistingImage = () => {
    setFormData(prev => ({
      ...prev,
      existingImage: null,
      error: "",
    }));
  };

  const handleRemoveFile = () => {
    setFormData({
      ...formData,
      file: null,
      error: "",
    });
  };

  // Video URL validation (YouTube only)
  const validateVideoUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  // Extract video ID from YouTube URL
  const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Fetch video data (YouTube only)
  const fetchVideoData = async (videoId) => {
    try {
      // Using YouTube oEmbed API for thumbnail and basic info
      const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      if (!response.ok) throw new Error('Video not found');
      return await response.json();
    } catch (error) {
      throw new Error('Unable to fetch video information');
    }
  };

  // Handle video URL input
  const handleVideoUrlChange = async (url) => {
    setVideoModal(prev => ({ ...prev, url, error: "" }));
    
    if (!url.trim()) {
      setVideoModal(prev => ({ ...prev, isValid: false, videoData: null }));
      return;
    }

    if (!validateVideoUrl(url)) {
      setVideoModal(prev => ({ 
        ...prev, 
        isValid: false, 
        videoData: null, 
        error: "Please enter a valid YouTube URL" 
      }));
      return;
    }

    const videoId = extractVideoId(url);
    if (!videoId) {
      setVideoModal(prev => ({ 
        ...prev, 
        isValid: false, 
        videoData: null, 
        error: "Unable to extract video ID from URL" 
      }));
      return;
    }

    setVideoModal(prev => ({ ...prev, loading: true }));
    
    try {
      const videoData = await fetchVideoData(videoId);
      setVideoModal(prev => ({ 
        ...prev, 
        isValid: true, 
        videoData: { ...videoData, videoId },
        loading: false,
        error: ""
      }));
    } catch (error) {
      setVideoModal(prev => ({ 
        ...prev, 
        isValid: false, 
        videoData: null, 
        loading: false,
        error: error.message 
      }));
    }
  };

  // Add video to post
  const addVideo = () => {
    if (videoModal.isValid && videoModal.videoData) {
      setFormData(prev => ({
        ...prev,
        youtubeVideo: videoModal.videoData,
        error: ""
      }));
      setVideoModal({
        isOpen: false,
        url: "",
        isValid: false,
        videoData: null,
        loading: false,
        error: ""
      });
    }
  };

  // Remove video
  const removeVideo = () => {
    setFormData(prev => ({
      ...prev,
      youtubeVideo: null,
      error: ""
    }));
  };

  const handleCommunityToggle = (communityId) => {
    setFormData(prev => {
      const isSelected = prev.selectedCommunities.includes(communityId);
      return {
        ...prev,
        selectedCommunities: isSelected
          ? prev.selectedCommunities.filter(id => id !== communityId)
          : [...prev.selectedCommunities, communityId]
      };
    });
  };

  const handleAudienceChange = (audience) => {
    setFormData(prev => ({
      ...prev,
      audience,
      selectedCommunities: audience === "Communities" ? prev.selectedCommunities : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { content, file, youtubeVideo, audience, selectedCommunities, allowComments } = formData;
    
    if (loading) return;

    if (!content.trim() && !file && !youtubeVideo && !formData.existingImage) {
      setFormData(prev => ({
        ...prev,
        error: "Please enter a message, select a file, or add a video link."
      }));
      return;
    }

    // Validate community selection for "Communities" audience
    if (audience === "Communities" && (!selectedCommunities || selectedCommunities.length === 0)) {
      const errorMessage = "Please select at least one community.";
      showError(errorMessage);
      setFormData(prev => ({
        ...prev,
        error: errorMessage
      }));
      return;
    }

    setLoading(true);

    try {
      const newPost = new FormData();
      newPost.append("content", content);
      if (file) {
        newPost.append("file", file);
      }
      newPost.append("audience", audience);
      newPost.append("allowComments", allowComments);

      // Add YouTube video data if present
      if (youtubeVideo) {
        newPost.append("youtubeVideo", JSON.stringify(youtubeVideo));
      }

      if (audience === "Communities" && selectedCommunities.length > 0) {
        newPost.append("selectedCommunities", JSON.stringify(selectedCommunities));
      }

      const result = await dispatch(updatePostAction(post._id, newPost));
      
      if (result && result.success) {
        showSuccess("Post published successfully!");
        onClose();
      } else {
        const errorMessage = result?.error || "Failed to publish post. Please try again.";
        showError(errorMessage);
      }
    } catch (error) {
      showError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cancelButtonRef = useRef(null);
  const videoInputRef = useRef(null);

  // Focus video input when modal opens
  useEffect(() => {
    if (videoModal.isOpen && videoInputRef.current) {
      setTimeout(() => {
        videoInputRef.current?.focus();
      }, 100);
    }
  }, [videoModal.isOpen]);

  return (
    <Transition.Root show={showModal} as="div">
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={videoModal.isOpen ? () => {} : onClose}
      >
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
               <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[95vw] h-[90vh] sm:w-[55vw] sm:h-[80vh] max-w-none flex flex-col">
                 {/* Modal Header */}
                 <div className="bg-white border-b border-gray-200 px-6 py-4 sm:px-8 sm:py-6 flex-shrink-0">
                   <div className="flex items-center justify-between">
                     <h3 className="text-xl font-semibold text-gray-900 font-roboto-flex">
                       Publish Post
                     </h3>
                     <button
                       onClick={onClose}
                       className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                     >
                       <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                       </svg>
                     </button>
                   </div>
                 </div>

                 {/* Modal Body */}
                 <div className="bg-white px-6 py-4 sm:px-8 sm:py-6 flex flex-col h-full">

                  <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
                     <div className="flex items-start gap-4 mb-4 flex-1 min-h-0 overflow-y-auto pr-2">
                      <div className="flex-shrink-0">
                        <img
                          src={JSON.parse(localStorage.getItem("profile"))?.user?.avatar || ""}
                          alt="Profile"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <textarea
                          className="w-full shadow-sm hover:shadow-md transition-shadow resize-none border border-gray-200 rounded-xl px-4 py-4 text-lg placeholder-gray-500 font-roboto-flex bg-gray-50 focus:bg-white focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all outline-none"
                          name="content"
                          id="content"
                          value={formData.content}
                          onChange={handleContentChange}
                          minLength={1}
                          maxLength={5000}
                          placeholder="What's on your mind?"
                           rows={2}
                          autoFocus
                        />
                        <div className="mt-1">
                          {(formData.file || formData.youtubeVideo || formData.existingImage) ? (
                            // File/Video Preview
                            <div className="space-y-2">
                              <div className="relative">
                                 <div className="w-full aspect-[21/9] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
                                   {formData.file ? (
                                     formData.file.type.startsWith('image/') ? (
                                       <img
                                         src={URL.createObjectURL(formData.file)}
                                         alt="Preview"
                                         className="w-full h-full object-cover"
                                       />
                                     ) : formData.file.type.startsWith('video/') ? (
                                       <video
                                         src={URL.createObjectURL(formData.file)}
                                         className="w-full h-full object-cover"
                                         muted
                                       />
                                     ) : (
                                       <svg
                                         xmlns="http://www.w3.org/2000/svg"
                                         className="h-12 w-12 text-gray-400"
                                         fill="currentColor"
                                         viewBox="0 0 24 24"
                                       >
                                         <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                                       </svg>
                                     )
                                   ) : formData.youtubeVideo ? (
                                     <div className="w-full h-full relative">
                                       <img
                                         src={formData.youtubeVideo.thumbnail_url}
                                         alt={formData.youtubeVideo.title}
                                         className="w-full h-full object-cover"
                                       />
                                       <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                         <FaVideo className="text-white text-4xl" />
                                       </div>
                                     </div>
                                   ) : formData.existingImage ? (
                                     isExistingImageFile ? (
                                       <img
                                         src={formData.existingImage}
                                         alt="Existing post"
                                         className="w-full h-full object-cover"
                                       />
                                     ) : (
                                       <video
                                         src={formData.existingImage}
                                         className="w-full h-full object-cover"
                                         muted
                                       />
                                     )
                                   ) : null}
                                 </div>
                                <button
                                  type="button"
                                  onClick={formData.file ? handleRemoveFile : formData.youtubeVideo ? removeVideo : handleRemoveExistingImage}
                                  className="absolute top-2 right-2 bg-action text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-action-hover transition-colors"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="space-y-1">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                                  <span className="text-xs text-gray-500 font-roboto-flex">
                                    {formData.content.length}/5000
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  {[
                                    { name: "Followers", icon: FaUserFriends },
                                    { name: "Communities", icon: FaUsers }
                                  ].map(({ name, icon: Icon }) => (
                                    <button
                                      key={name}
                                      type="button"
                                      onClick={() => handleAudienceChange(name)}
                                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${
                                        formData.audience === name
                                          ? "bg-primary text-white"
                                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      {name}
                                    </button>
                                  ))}
                                </div>
                                {/* Communities Selection */}
                                {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                      Select communities:
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {joinedCommunities.map((community) => (
                                        <button
                                          key={community._id}
                                          type="button"
                                          onClick={() => handleCommunityToggle(community._id)}
                                          className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${
                                            formData.selectedCommunities.includes(community._id)
                                              ? "bg-primary text-white border-primary"
                                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                          }`}
                                        >
                                          {community.name}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            // Add photo/video buttons
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                  <label
                                    htmlFor="file"
                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4 text-green-500"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                    </svg>
                                     <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add photo/video</span>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setVideoModal(prev => ({ ...prev, isOpen: true }))}
                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                                  >
                                    <FaVideo className="h-4 w-4 text-action" />
                                    <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add video link</span>
                                  </button>
                                </div>
                                <span className="text-xs text-gray-500 font-roboto-flex">
                                  {formData.content.length}/5000
                                </span>
                              </div>
                              <div className="space-y-1">
                                <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                                <div className="flex gap-2">
                                  {[
                                    { name: "Followers", icon: FaUserFriends },
                                    { name: "Communities", icon: FaUsers },
                                    { name: "Anyone", icon: FaGlobe },
                                    { name: "Private", icon: FaLock }
                                  ].map(({ name, icon: Icon }) => (
                                    <button
                                      key={name}
                                      type="button"
                                      onClick={() => handleAudienceChange(name)}
                                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${
                                        formData.audience === name
                                          ? "bg-primary text-white"
                                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      {name}
                                    </button>
                                  ))}
                                </div>
                                {/* Communities Selection */}
                                {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                      Select communities:
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {joinedCommunities.map((community) => (
                                        <button
                                          key={community._id}
                                          type="button"
                                          onClick={() => handleCommunityToggle(community._id)}
                                          className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${
                                            formData.selectedCommunities.includes(community._id)
                                              ? "bg-primary text-white border-primary"
                                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                          }`}
                                        >
                                          {community.name}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Comments Toggle */}
                                <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                                    <span className="text-xs text-gray-500 font-roboto-flex">
                                      {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                      !formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        !formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <input
                      name="file"
                      type="file"
                      id="file"
                      accept={fileUploadSettings.allowedFileTypes.length > 0 ? fileUploadSettings.allowedFileTypes.map(type => `${type}/*`).join(', ') : ''}
                      onChange={handleFileChange}
                      disabled={!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length}
                      className="hidden"
                    />
                    
                    {/* Error Message */}
                    {formData.error && (
                      <div className="text-action text-sm font-roboto-flex mt-2">
                        {formData.error}
                      </div>
                    )}
                    
                     {/* Modal Footer */}
                     <div className="border-t border-gray-200 mt-6 pt-6 flex-shrink-0 bg-white">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 font-roboto-flex"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading || (!formData.content.trim() && !formData.file && !formData.youtubeVideo && !formData.existingImage)}
                          className="px-6 py-2.5 rounded-lg text-sm font-medium transition-colors bg-primary-600 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed font-roboto-flex"
                        >
                          {loading ? "Publishing..." : "Publish Post"}
                        </button>
                      </div>
                    </div>
                  </form>
                 </div>
               </div>
            </Transition.Child>
          </div>
        </div>
        {/* Video Link Modal - Inside main Dialog */}
        {videoModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 font-roboto-flex">Add Video Link</h3>
                  <button
                    onClick={() => setVideoModal({
                      isOpen: false,
                      url: "",
                      isValid: false,
                      videoData: null,
                      loading: false,
                      error: ""
                    })}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="video-url" className="block text-sm font-medium text-gray-700 font-roboto-flex mb-2">
                      Video URL
                    </label>
                    <input
                      type="text"
                      id="video-url"
                      ref={videoInputRef}
                      value={videoModal.url}
                      onChange={(e) => handleVideoUrlChange(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-roboto-flex"
                      autoFocus
                      disabled={videoModal.loading}
                    />
                    {videoModal.error && (
                      <p className="mt-1 text-sm text-action font-roboto-flex">{videoModal.error}</p>
                    )}
                    <div className="mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <p className="text-xs text-gray-500 font-roboto-flex">
                        Please paste only YouTube video link.
                      </p>
                    </div>
                  </div>

                  {videoModal.loading && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-gray-600 font-roboto-flex">Loading video...</span>
                    </div>
                  )}

                  {videoModal.isValid && videoModal.videoData && (
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-gray-700 font-roboto-flex">Preview:</div>
                      <div className="relative">
                        <img
                          src={videoModal.videoData.thumbnail_url}
                          alt={videoModal.videoData.title}
                          className="w-full rounded-lg"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                          <FaVideo className="text-white text-3xl" />
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 font-roboto-flex">
                        <div className="font-roboto-flex">{videoModal.videoData.title}</div>
                        <div className="text-xs text-gray-500 font-roboto-flex">by {videoModal.videoData.author_name}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setVideoModal({
                        isOpen: false,
                        url: "",
                        isValid: false,
                        videoData: null,
                        loading: false,
                        error: ""
                      })}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-roboto-flex"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addVideo}
                      disabled={!videoModal.isValid || videoModal.loading}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-roboto-flex"
                    >
                      Add Video
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </Transition.Root>
  );
});

PublishPostModal.displayName = "PublishPostModal";

export default PublishPostModal;
